/**
 * useDashboard — the React binding for the Dashboard landing page.
 *
 * Strict layering: the page calls THIS hook; the hook composes the feature
 * STORES (Template/Room/Task/Estimate/Chat), each of which talks to the api
 * facade. The page never imports a store or the api directly.
 *
 * The dashboard is a pure read aggregation: it loads every collection's
 * summaries once (in parallel), computes headline counts, and merges the most
 * recent items across features into a single activity feed. It owns no
 * persistence.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { TemplateStore } from '../templates/templateStore';
import { RoomStore } from '../rooms/roomStore';
import { TaskStore } from '../tasks/taskStore';
import { EstimateStore } from '../estimation/estimateStore';
import { ChatStore } from '../chat/chatStore';
import { modelLabel } from '../chat/chatModel';
import type { ActivityItem, DashboardCounts } from './types';

/** How many recent-activity items to surface. */
const ACTIVITY_LIMIT = 8;

export interface UseDashboardResult {
  counts: DashboardCounts;
  activity: ActivityItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const EMPTY_COUNTS: DashboardCounts = {
  templates: 0,
  templateVersions: 0,
  rooms: 0,
  tasks: 0,
  estimates: 0,
  conversations: 0,
};

export function useDashboard(): UseDashboardResult {
  const api = useApi();
  const stores = useMemo(
    () => ({
      templates: new TemplateStore(api),
      rooms: new RoomStore(api),
      tasks: new TaskStore(api),
      estimates: new EstimateStore(api),
      chat: new ChatStore(api),
    }),
    [api],
  );

  const [counts, setCounts] = useState<DashboardCounts>(EMPTY_COUNTS);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [templates, rooms, tasks, estimates, conversations] =
        await Promise.all([
          stores.templates.listSummaries(),
          stores.rooms.listSummaries(),
          stores.tasks.listAll(),
          stores.estimates.listSummaries(),
          stores.chat.listSummaries(),
        ]);

      const distinctTemplateNames = new Set(templates.map((t) => t.name));

      setCounts({
        templates: distinctTemplateNames.size,
        templateVersions: templates.length,
        rooms: rooms.length,
        tasks: tasks.length,
        estimates: estimates.length,
        conversations: conversations.length,
      });

      const items: ActivityItem[] = [
        ...templates.map((t) => ({
          key: `template:${t.id}`,
          kind: 'template' as const,
          title: `${t.name} v${t.version}`,
          subtitle: t.createdBy ? `by ${t.createdBy}` : undefined,
          timestamp: t.updatedAt,
          href: `/templates/${encodeURIComponent(t.id)}`,
        })),
        ...rooms.map((r) => ({
          key: `room:${r.id}`,
          kind: 'room' as const,
          title: r.name,
          subtitle: `${r.memberCount} ${r.memberCount === 1 ? 'member' : 'members'}`,
          timestamp: r.createdAt,
          href: `/rooms/${encodeURIComponent(r.id)}`,
        })),
        ...tasks.map((t) => ({
          key: `task:${t.id}`,
          kind: 'task' as const,
          title: t.title,
          subtitle: t.status,
          timestamp: t.updatedAt,
          href: '/tasks',
        })),
        ...estimates.map((e) => ({
          key: `estimate:${e.id}`,
          kind: 'estimate' as const,
          title: e.title,
          subtitle: e.createdBy ? `by ${e.createdBy}` : undefined,
          timestamp: e.createdAt,
          href: `/estimation/${encodeURIComponent(e.id)}`,
        })),
        ...conversations.map((c) => ({
          key: `conversation:${c.id}`,
          kind: 'conversation' as const,
          title: c.title,
          subtitle: modelLabel(c.modelId),
          timestamp: c.updatedAt,
          href: '/chat',
        })),
      ];

      items.sort((a, b) => (b.timestamp ?? '').localeCompare(a.timestamp ?? ''));
      setActivity(items.slice(0, ACTIVITY_LIMIT));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load dashboard.');
    } finally {
      setLoading(false);
    }
  }, [stores]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { counts, activity, loading, error, refresh };
}
